import type { CustomerTeamMemberWithLabels } from '@clockwise/clients/customer-team';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { DetailRow } from './detail-list';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: CustomerTeamMemberWithLabels | null;
  /** Invoked when the viewer clicks Edit; the parent swaps in the form sheet.
   *  Omitted when the caller may not edit this customer's records — the Edit
   *  button shows only when it is given. */
  onEdit?: () => void;
}

/**
 * Read-only detail for a single team membership, opened by clicking a row in
 * the team tab. The Edit action shows only when the panel passes `onEdit`.
 */
export function CustomerTeamMemberViewSheet({ open, onOpenChange, member, onEdit }: Props) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Team member</SheetTitle>
          <SheetDescription>Membership details for this customer’s team.</SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {member ? (
            <dl className="grid grid-cols-1 sm:grid-cols-[140px_1fr]">
              <DetailRow label="User">
                <span className="font-medium">{member.userName ?? 'Unknown user'}</span>
                {member.teamLead ? (
                  <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/30 dark:text-amber-200">
                    Lead
                  </span>
                ) : null}
              </DetailRow>
              <DetailRow label="Service role">{member.serviceRoleName ?? '—'}</DetailRow>
              <DetailRow label="Team lead">{member.teamLead ? 'Yes' : 'No'}</DetailRow>
              <DetailRow label="Effective from">{member.effectiveFrom}</DetailRow>
              <DetailRow label="Effective to">{member.effectiveTo ?? 'Current'}</DetailRow>
              <DetailRow label="Created">{new Date(member.createdAt).toLocaleString()}</DetailRow>
              <DetailRow label="Updated">{new Date(member.updatedAt).toLocaleString()}</DetailRow>
            </dl>
          ) : null}
        </div>

        <SheetFooter className="flex-row justify-end border-t">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {onEdit ? (
            <Button type="button" onClick={onEdit}>
              <Pencil />
              Edit
            </Button>
          ) : null}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
